import React from 'react';
import { Badge } from 'react-bootstrap';

import { Grid } from '../../../common/components/Grid/Grid';
import {
  GridCountFunction,
  GridGetterFunction,
  GridRecord,
} from '../../../common/components/Grid/GridDataTypes';
import momencik from '../../../common/momencik';
import { GetJudgmentP1DocumentDto } from '../../../common/spdCore/autogenerated/spdApiClient';
import {
  SimP1DocumentTypeEnum,
  SimP1StatusEnum,
  SimP1StatusEnumText,
} from '../../../common/spdCore/validation/enums';
import { ArticleMode } from '../../../common/spdCore/validation/schemas';
import { ExaminedFromExamPreviewLink } from '../Examined/ExaminedFromExamPreviewLink';
import { ExamLink } from '../Judgments/ExamLink';
import { JudgmentsLink } from '../Judgments/JudgmentsLink';

import { RetryP1DocumentButton } from './RetryP1DocumentButton';

interface SimP1DocumentsSearchProps {
  getData: GridGetterFunction;
  getCount: GridCountFunction;
  refresh?: boolean;
  withFilterInColumns?: boolean;
}

const JudgmentNumberCell: React.FC<{ doc: GetJudgmentP1DocumentDto }> = ({
  doc,
}) => {
  const examId = doc.judgment?.exam?.id;
  const mode = doc.judgment?.article?.mode as unknown as ArticleMode;
  if (!examId) {
    return <>{doc.judgment?.number ?? '—'}</>;
  }
  return (
    <>
      <JudgmentsLink examId={examId} articleMode={mode} />
      {doc.judgment?.number}
    </>
  );
};

const STATUS_BADGE_BG: Record<SimP1StatusEnum, string> = {
  [SimP1StatusEnum.ToSign]: 'primary',
  [SimP1StatusEnum.WaitingForP1]: 'secondary',
  [SimP1StatusEnum.AcceptedInP1]: 'success',
  [SimP1StatusEnum.RejectedInP1]: 'danger',
  [SimP1StatusEnum.ValidationFailed]: 'warning',
};

const StatusCell: React.FC<{ doc: GetJudgmentP1DocumentDto }> = ({ doc }) => {
  const status = doc.status as unknown as SimP1StatusEnum | null;
  if (!status) {
    return <>{'—'}</>;
  }
  const isCancellation =
    (doc.documentType as unknown as SimP1DocumentTypeEnum) ===
    SimP1DocumentTypeEnum.Cancellation;
  return (
    <Badge bg={STATUS_BADGE_BG[status]}>
      {isCancellation ? 'Anulowanie: ' : ''}
      {SimP1StatusEnumText[status]}
    </Badge>
  );
};

const ExamNumberCell: React.FC<{ doc: GetJudgmentP1DocumentDto }> = ({
  doc,
}) => {
  const examId = doc.judgment?.exam?.id;
  const mode = doc.judgment?.article?.mode as unknown as ArticleMode;
  if (!examId) {
    return <>{'—'}</>;
  }
  return (
    <>
      <ExamLink examId={examId} articleMode={mode} />
      {doc.judgment?.exam?.number}
    </>
  );
};

export const simP1DocumentForGrid = (
  doc: GetJudgmentP1DocumentDto,
  onRetry?: () => void,
): GridRecord => ({
  key: doc.id,
  values: {
    'judgment.number': <JudgmentNumberCell doc={doc} />,
    'exam.number': <ExamNumberCell doc={doc} />,
    'article.description': doc.judgment?.article?.description ?? '—',
    'examined.surname': doc.judgment?.exam?.examined ? (
      <>
        <ExaminedFromExamPreviewLink examId={doc.judgment.exam.id} />{' '}
        {doc.judgment.exam.examined.surname}{' '}
        {doc.judgment.exam.examined.firstname}
      </>
    ) : (
      '—'
    ),
    'doc.sentVersionNumber': doc.sentVersionNumber,
    'doc.status': <StatusCell doc={doc} />,
    'doc.error': doc.errorMessage ?? doc.error ?? '—',
    'doc.creationTs': doc.creationTs
      ? momencik(doc.creationTs, 'YYYY-MM-DD HH:mm')
      : '—',
    'doc.action':
      (doc.status as unknown as SimP1StatusEnum) ===
        SimP1StatusEnum.RejectedInP1 && doc.isLatest ? (
        <RetryP1DocumentButton documentId={doc.id} onSuccess={onRetry} />
      ) : null,
  },
});

export const SimP1DocumentsSearch: React.FC<
  SimP1DocumentsSearchProps
> = props => {
  const columns = [
    { header: 'Data utworzenia', property: 'doc.creationTs' },
    { header: 'Nr orzeczenia', property: 'judgment.number' },
    { header: 'Nr badania', property: 'exam.number' },
    { header: 'Podstawa', property: 'article.description', noSorting: true },
    { header: 'Badany', property: 'examined.surname', noSorting: true },
    { header: 'Wersja', property: 'doc.sentVersionNumber' },
    { header: 'Status', property: 'doc.status', noSorting: true },
    { header: 'Błąd', property: 'doc.error' },
    {
      header: 'Akcja',
      property: 'doc.action',
      noSorting: true,
      noFilter: true,
    },
  ];

  return (
    <Grid
      getData={props.getData}
      getDataCount={props.getCount}
      columns={columns}
      showFilter={true}
      initialOrderColumn="doc.creationTs"
      initialOrderDirection="DESC"
      availablePageSizes={[10, 20, 50]}
      emptyGridMessage="Brak dokumentów SIM P1"
      refresh={props.refresh}
      withFilterInColumns={props.withFilterInColumns}
    />
  );
};
